// @ts-nocheck
import React, { useState } from 'react';

interface DiscoveredFeed {
  url: string;
  title: string;
  type?: string;
}

interface AddFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: (data: { feedUrl: string; title?: string; refreshIntervalMinutes?: number }) => Promise<any>;
  onDiscover: (url: string) => Promise<DiscoveredFeed[]>;
}

export default function AddFeedModal({
  isOpen,
  onClose,
  onSubscribe,
  onDiscover,
}: AddFeedModalProps) {
  const [urlInput, setUrlInput] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [discoveredFeeds, setDiscoveredFeeds] = useState<DiscoveredFeed[]>([]);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const handleDiscoverOrSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;

    setErrorMessage('');
    const target = urlInput.trim();

    // If it looks like a direct xml/rss feed, subscribe directly
    if (target.endsWith('.xml') || target.endsWith('/feed') || target.endsWith('/rss')) {
      handleDirectSubscribe(target);
      return;
    }

    // Otherwise discover feeds from page
    setIsDiscovering(true);
    try {
      const feeds = await onDiscover(target);
      if (feeds && feeds.length > 0) {
        setDiscoveredFeeds(feeds);
      } else {
        // Fallback to subscribing directly
        await handleDirectSubscribe(target);
      }
    } catch (err: any) {
      // If discovery failed, try direct subscribe
      try {
        await handleDirectSubscribe(target);
      } catch (subErr: any) {
        setErrorMessage(subErr.message || 'Could not find a valid RSS/Atom feed at this address.');
      }
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleDirectSubscribe = async (feedUrl: string, title?: string) => {
    setIsSubscribing(true);
    setErrorMessage('');
    try {
      await onSubscribe({
        feedUrl,
        title: customTitle || title,
        refreshIntervalMinutes: refreshInterval,
      });
      setUrlInput('');
      setCustomTitle('');
      setDiscoveredFeeds([]);
      onClose();
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to subscribe to feed.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="reader-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="reader-modal-header">
          <h3 className="reader-modal-title">Subscribe to RSS / Atom Feed</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--reader-text-muted)' }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleDiscoverOrSubmit}>
          <div className="reader-modal-body">
            {errorMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid #ef4444',
                  borderRadius: '4px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                }}
              >
                {errorMessage}
              </div>
            )}

            <div className="reader-form-group">
              <label className="reader-form-label">Feed or Website URL *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="url"
                  className="reader-input"
                  placeholder="https://techcrunch.com, https://daringfireball.net/feeds/json..."
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  style={{ flex: 1 }}
                  autoFocus
                  required
                  disabled={isDiscovering || isSubscribing}
                />
                <button
                  type="submit"
                  className="reader-btn"
                  disabled={isDiscovering || isSubscribing || !urlInput.trim()}
                >
                  {isDiscovering ? 'Discovering…' : 'Find Feed'}
                </button>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--reader-text-subtle)' }}>
                You can enter a direct RSS feed URL or any website URL. We will automatically detect available feeds.
              </span>
            </div>

            {/* Discovered Feeds List */}
            {discoveredFeeds.length > 0 && (
              <div className="reader-form-group">
                <label className="reader-form-label">Available Feeds Found:</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {discoveredFeeds.map((feed, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '10px 12px',
                        border: '1px solid var(--reader-border)',
                        borderRadius: '4px',
                        background: 'var(--reader-hover)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{feed.title}</div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: 'var(--reader-text-subtle)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {feed.url}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="reader-btn reader-btn-primary"
                        style={{ fontSize: '0.78rem', padding: '3px 10px', height: '28px' }}
                        disabled={isSubscribing}
                        onClick={() => handleDirectSubscribe(feed.url, feed.title)}
                      >
                        Subscribe
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="reader-form-group">
              <label className="reader-form-label">Custom Feed Title (optional)</label>
              <input
                type="text"
                className="reader-input"
                placeholder="Leave blank to use feed's default title"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                disabled={isSubscribing}
              />
            </div>

            <div className="reader-form-group">
              <label className="reader-form-label">Refresh Interval</label>
              <select
                className="reader-input"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                disabled={isSubscribing}
              >
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
                <option value={60}>Every 1 hour (Default)</option>
                <option value={360}>Every 6 hours</option>
                <option value={1440}>Every 24 hours</option>
              </select>
            </div>
          </div>

          <div className="reader-modal-footer">
            <button type="button" className="reader-btn" onClick={onClose} disabled={isSubscribing}>
              Cancel
            </button>
            <button
              type="submit"
              className="reader-btn reader-btn-primary"
              disabled={isSubscribing || isDiscovering || !urlInput.trim()}
            >
              {isSubscribing ? 'Subscribing…' : 'Subscribe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
